import * as Switch from '@radix-ui/react-switch'
import type { ReactNode } from 'react'

import { cn } from './cn'

/**
 * 开关。Skill 逐个开关、排班开关、「到期自动续」都用它。
 *
 * Radix 只负责无障碍（键盘、aria-checked、label 关联），
 * **外观 100% 自己按 token 写**——组件库的默认样式跟这套设计一定打架。
 */
export interface ToggleProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  /** 开关旁边的文字。给了就自动关联 label，点文字也能切。 */
  label?: ReactNode
  /** 第二行的小字，说清打开之后会发生什么。 */
  hint?: ReactNode
  id?: string
  className?: string
}

export function Toggle({
  checked,
  onCheckedChange,
  disabled,
  label,
  hint,
  id,
  className,
}: ToggleProps) {
  const switchId = id ?? `npc-toggle-${label ? String(label).slice(0, 12) : 'x'}`

  const control = (
    <Switch.Root
      id={switchId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'relative h-[20px] w-[34px] shrink-0 rounded-npc-chip cursor-pointer',
        'transition-colors duration-npc-hover',
        'bg-npc-ink-faint data-[state=checked]:bg-npc-green',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      <Switch.Thumb
        className={cn(
          'block size-[16px] rounded-npc-full bg-npc-panel shadow-npc-card',
          'translate-x-[2px] transition-transform duration-npc-hover',
          'data-[state=checked]:translate-x-[16px]',
        )}
      />
    </Switch.Root>
  )

  if (!label && !hint) {
    return <div className={className}>{control}</div>
  }

  return (
    <div className={cn('flex items-start gap-npc-gap', className)}>
      {control}
      <div className="min-w-0">
        {label ? (
          <label htmlFor={switchId} className="text-npc-body cursor-pointer select-none">
            {label}
          </label>
        ) : null}
        {hint ? <div className="text-npc-caption text-npc-ink-soft">{hint}</div> : null}
      </div>
    </div>
  )
}
